import EnvelopeAnimation from './EnvelopeAnimation';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { trackRSVPEvent, trackPageView, EVENT_TYPES } from '../utils/analytics';

const RSVPForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const invitationCode = searchParams.get('code');
  const { t } = useLanguage();


  const [familyGroup, setFamilyGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [countdown, setCountdown] = useState(10);

  // Countdown effect for success modal
  useEffect(() => {
    if (showSuccessModal && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showSuccessModal && countdown === 0) {
      // Navigate back to home page with language awareness
      const currentPath = location.pathname;
      const basePath = currentPath.startsWith('/es') ? '/es' : '/';
      navigate(basePath);
    }
  }, [showSuccessModal, countdown, navigate]);
  
  const [formData, setFormData] = useState({
    submittedByMemberId: '',
    attendingMembers: [],
    message: ''
  });

  useEffect(() => {
    if (invitationCode) {
      loadFamilyGroup(invitationCode);
    }
    
    // Track RSVP page view
    trackPageView(location.pathname, 'RSVP Form');
  }, [invitationCode, location.pathname]);

  // Track when family group is loaded
  useEffect(() => {
    if (familyGroup) {
      trackRSVPEvent(EVENT_TYPES.RSVP_FORM_STARTED, {
        invitation_code: invitationCode || 'direct_access',
        family_group: familyGroup.groupName,
        family_size: familyGroup.members.length
      });
    }
  }, [familyGroup, invitationCode]);

  const loadFamilyGroup = async (code) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/family-group/${code}`);
      const result = await response.json();
      
      if (result.success) {
        setFamilyGroup(result.data);
        // Find primary contact or use first member as default
        const primaryContact = result.data.members.find(member => member.isPrimaryContact) || result.data.members[0];
        
        // Initialize form data with primary contact as submitter
        setFormData(prev => ({
          ...prev,
          submittedByMemberId: primaryContact._id,
          attendingMembers: result.data.members.map(member => ({
            memberId: member._id,
            firstName: member.firstName,
            lastName: member.lastName,

            attending: 'yes',
            dietaryRestrictions: ''
          }))
        }));
      } else {
        setError(result.message || 'Invalid invitation code');
      }
    } catch (error) {
      console.error('Error loading family group:', error);
      setError('Failed to load invitation details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitterChange = (e) => {
    const memberId = e.target.value;
    setFormData(prev => ({
      ...prev,
      submittedByMemberId: memberId
    }));
  };

  const handleMemberChange = (memberId, field, value) => {
    setFormData(prev => ({
      ...prev,
      attendingMembers: prev.attendingMembers.map(member => 
        member.memberId === memberId 
          ? { ...member, [field]: value }
          : member
      )
    }));
  };

  const handleMessageChange = (e) => {
    setFormData(prev => ({
      ...prev,
      message: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Find the selected member for submittedBy
      const selectedMember = familyGroup.members.find(member => member._id === formData.submittedByMemberId);
      const submittedBy = {
        firstName: selectedMember.firstName,
        lastName: selectedMember.lastName,

      };

      const response = await fetch('/api/rsvp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invitationCode,
          submittedBy,
          attendingMembers: formData.attendingMembers,
          message: formData.message
        })
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setShowSuccessModal(true);
        setFormData(prev => ({
          ...prev,
          message: ''
        }));
        
        // Track successful RSVP submission
        trackRSVPEvent(EVENT_TYPES.RSVP_FORM_COMPLETED, {
          family_group: familyGroup.groupName,
          attending_count: formData.attendingMembers.filter(m => m.attending === 'yes').length,
          total_members: formData.attendingMembers.length,
          has_message: formData.message.length > 0
        });
      } else {
        setSubmitStatus('error');
        
        // Track failed RSVP submission
        trackRSVPEvent(EVENT_TYPES.RSVP_FORM_ABANDONED, {
          family_group: familyGroup.groupName,
          error: result.message || 'Unknown error'
        });
      }
    } catch (error) {
      console.error('RSVP submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusMessage = () => {
    switch (submitStatus) {
      case 'error':
        return { text: t('rsvp.errorMessage'), type: 'danger' };
      default:
        return null;
    }
  };

  if (loading) {
    return (
    <EnvelopeAnimation>
      <section id="rsvp" className="py-5 bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('common.loading')}</span>
              </div>
              <p className="mt-3">{t('rsvp.loading')}</p>
            </div>
          </div>
        </div>
      </section>
    </EnvelopeAnimation>
    );
  }

  if (error) {
    return (
    <EnvelopeAnimation>
      <section id="rsvp" className="py-5 bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="alert alert-danger text-center">
                <h4>{t('rsvp.invalidInvitation')}</h4>
                <p>{error}</p>
                <p>{t('rsvp.invalidMessage')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </EnvelopeAnimation>
    );
  }

  if (!familyGroup) {
    return (
    <EnvelopeAnimation>
      <section id="rsvp" className="py-5 bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <h2 className="section-title">{t('rsvp.title')}</h2>
              <p className="lead">{t('rsvp.useInvitationLink')}</p>
            </div>
          </div>
        </div>
      </section>
    </EnvelopeAnimation>
    );
  }

  return (
    <EnvelopeAnimation>
      <div className="rsvp-invitation-page">
        {/* Invitation Hero Section */}
        <div className="invitation-hero-section d-flex align-items-center justify-content-center">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10">
                {/* Invitation Card */}
                <motion.div 
                  className="invitation-card"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1 }}
                >
                  

                  {/* Card Content */}
                  <div className="invitation-content">
                    <div className="row align-items-center">
                      {/* Vertical Photo */}
                      <div className="col-md-5 col-lg-4 mb-4 mb-md-0">
                        <motion.div 
                          className="photo-container"
                          initial={{ opacity: 0, x: -50 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.8, delay: 1 }}
                        >
                          <div className="invitation-photo-wrapper">
                            <img 
                              src="/images/fotos/form.jpeg"
                              alt={t('rsvp.personalPhoto.alt', { familyName: familyGroup.groupName })}
                              className="invitation-photo"
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "block";
                              }}
                            />
                            <motion.div 
                              className="photo-date-overlay"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.8, delay: 1.4 }}
                            >
                              <h3 className="photo-date-text">{t('hero.date')}</h3>
                            </motion.div>
                          </div>
                          <div 
                            className="photo-placeholder d-none text-center p-4"
                            style={{ 
                              minHeight: "500px", 
                              display: "flex", 
                              alignItems: "center", 
                              justifyContent: "center",
                              background: "linear-gradient(135deg, var(--primary-color), var(--dusty-blue))",
                              borderRadius: "20px"
                            }}
                          >
                            <div className="text-white">
                              <i className="fas fa-heart fa-4x mb-3"></i>
                              <h4>Cami & Eze</h4>
                              <p className="mb-0">{t('rsvp.personalPhoto.placeholder', { familyName: familyGroup.groupName })}</p>
                            </div>
                          </div>
                        </motion.div>
                      </div>
                      
                      {/* Invitation Text */}
                      <div className="col-md-7 col-lg-8">
                        <motion.div 
                          className="invitation-text"
                          initial={{ opacity: 0, x: 50 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.8, delay: 1.2 }}
                        >

{/* Card Header */}
<div className="invitation-header text-center">
                    <motion.h1 
                      className="invitation-title mb-3"
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 1, delay: 0.4 }}
                    >
                      {t(`rsvp.invitation.greeting.${familyGroup.members.length === 1 ? 'singular' : 'plural'}`, { familyName: familyGroup.groupName })}
                    </motion.h1>
                    
                    
                      </div>

                            <h3 className="invitation-message mb-4">
                              {t(`rsvp.invitation.message.${familyGroup.members.length === 1 ? 'singular' : 'plural'}`)}
                            </h3>
                          
                          <div className="invitation-divider mb-4"></div>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Scroll Down Arrow */}
                <motion.div 
                  className="scroll-indicator text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.6 }}
                >
                  <div className="scroll-arrow-container">
                    <motion.div
                      className="scroll-arrow"
                      animate={{ y: [0, 10, 0] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <i className="fas fa-chevron-down"></i>
                    </motion.div>
                    <p className="scroll-text">{t('rsvp.scrollToForm')}</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* RSVP Form Section */}
        <div className="rsvp-form-section py-5">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <motion.div 
                  className="rsvp-form-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <div className="form-header text-center mb-4">
                    <h3 className="form-title">{t('rsvp.title')}</h3>
                    <p className="form-subtitle">{t('rsvp.subtitle')}</p>
                  </div>


                  <form onSubmit={handleSubmit}>
                    {/* Submitter Information */}
                    <div className="mb-4">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label htmlFor="submitterSelect" className="form-label">
                            <i className="fas fa-user-edit me-2"></i>
                            {t('rsvp.whoIsSubmitting')} {t('common.required')}
                          </label>
                          <select
                            className="form-select"
                            id="submitterSelect"
                            value={formData.submittedByMemberId}
                            onChange={handleSubmitterChange}
                            required
                          >
                            <option value="">{t('rsvp.selectSubmitter')}</option>
                            {familyGroup?.members.map((member) => (
                              <option key={member._id} value={member._id}>
                                {member.firstName} {member.lastName}
                                {member.isPrimaryContact && ` (${t('rsvp.primaryContact')})`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Family Members */}
                    <div className="mb-4">
                      <h5 className="mb-3">{t('rsvp.familyMembers')}</h5>
                      {formData.attendingMembers.map((member, index) => (
                        <div key={member.memberId} className="member-card mb-3">
                          <div className="member-content">
                            <div className="row align-items-center g-3">
                              <div className="col-12 col-md-1 col-sm-1 text-center text-md-end">
                                <i className="fas fa-user fa-2x"></i>
                              </div>
                              <div className="col-12 col-md-3 col-sm-11">
                                <h5 className="mb-0 text-center text-md-start">
                                  {member.firstName} {member.lastName}
                                </h5>
                              </div>
                              <div className="col-12 col-md-4">
                                <div className="d-flex justify-content-center justify-content-md-start flex-wrap gap-2">
                                  <div className="form-check">
                                    <input 
                                      className="form-check-input" 
                                      type="radio" 
                                      name={`attending_${member.memberId}`}
                                      id={`attending_yes_${member.memberId}`}
                                      value="yes"
                                      checked={member.attending === 'yes'}
                                      onChange={(e) => handleMemberChange(member.memberId, 'attending', e.target.value)}
                                    />
                                    <label className="form-check-label" htmlFor={`attending_yes_${member.memberId}`}>
                                      {t('rsvp.attending')}
                                    </label>
                                  </div>
                                  <div className="form-check">
                                    <input 
                                      className="form-check-input" 
                                      type="radio" 
                                      name={`attending_${member.memberId}`}
                                      id={`attending_no_${member.memberId}`}
                                      value="no"
                                      checked={member.attending === 'no'}
                                      onChange={(e) => handleMemberChange(member.memberId, 'attending', e.target.value)}
                                    />
                                    <label className="form-check-label" htmlFor={`attending_no_${member.memberId}`}>
                                      {t('rsvp.notAttending')}
                                    </label>
                                  </div>
                                </div>
                              </div>
                              <div className="col-12 col-md-4">
                                <textarea 
                                  type="text" 
                                  className="form-control form-control-sm" 
                                  placeholder={t('rsvp.dietaryRestrictions')}
                                  value={member.dietaryRestrictions}
                                  onChange={(e) => handleMemberChange(member.memberId, 'dietaryRestrictions', e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Message */}
                    <div className="mb-4">
                      <label htmlFor="message" className="form-label">{t('rsvp.message')}</label>
                      <textarea 
                        className="form-control" 
                        id="message"
                        rows="3"
                        placeholder={t('rsvp.messagePlaceholder')}
                        value={formData.message}
                        onChange={handleMessageChange}
                      />
                    </div>

{/* Adult Only Notice */}
{/* <motion.div 
                    className="adult-only-notice p-3 mb-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                  >
                    <div className="d-flex align-items-start">
                      <i className="fas fa-info-circle me-3 mt-1"></i>
                      <div>
                        <p className="mb-2">
                          <strong>{t('rsvp.invitation.adultOnly')}</strong>
                        </p>
                        
                      </div>
                    </div>
                  </motion.div> */}

                    {/* Submit Button */}
                    <div className="text-center">
                      <button 
                        type="submit" 
                        className="btn btn-primary btn-lg px-5"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {t('rsvp.submitting')}
                          </>
                        ) : (
                          t('rsvp.submitRsvp')
                        )}
                      </button>
                    </div>

                    {/* Status Message */}
                    {getStatusMessage() && (
                      <div className={`alert alert-${getStatusMessage().type} mt-3 text-center`}>
                        {getStatusMessage().text}
                      </div>
                    )}
                  </form>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="modal show d-block success-modal" style={{backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center">
                  <div className="mb-4">
                    <i className="fas fa-check-circle text-success"></i>
                  </div>
                  <h4 className="text-success mb-3">
                    {formData.attendingMembers.every(member => member.attending === 'no') 
                      ? t('rsvp.successMessageNoOneAttending')
                      : t('rsvp.successMessage')
                    }
                  </h4>
                  <div className="mb-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                  <p className="text-muted countdown-text">
                    <i className="fas fa-clock me-1"></i>
                    {t('rsvp.redirectingIn')} <strong>{countdown}</strong> {t('rsvp.seconds')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </EnvelopeAnimation>
  );
};

export default RSVPForm;
